import { USER_ROLES } from './ExitPlanPage.constants';
import { normalizePlanType, EMPTY_EXEMPTION_FIELDS } from '@Utils/planType';

/**
 * Validates if support configuration is valid
 */
export const isSupportValid = (supportState) => {
    if (!supportState.needsSupport) return true;
    if (supportState.marketingSupport && supportState.selectedMarketingMember) return true;
    if (supportState.supplyChainSupport && supportState.selectedSupplyChainMember) return true;
    return false;
};

/**
 * Checks if support team is selected but no member is chosen
 */
export const isSupportTeamSelectedButNoMember = (supportState) => {
    if (!supportState.needsSupport) return false;
    if (supportState.marketingSupport && !supportState.selectedMarketingMember) return true;
    if (supportState.supplyChainSupport && !supportState.selectedSupplyChainMember) return true;
    return false;
};

/**
 * Gets the next available plan number for naming
 */
export const getNextPlanNumber = (createdPlans) => {
    const usedNumbers = createdPlans
        .map(plan => {
            const match = plan.name && plan.name.match(/^Plan #(\d+)$/);
            return match ? parseInt(match[1], 10) : null;
        })
        .filter(n => n !== null)
        .sort((a, b) => a - b);

    let next = 1;
    for (let i = 0; i < usedNumbers.length; i++) {
        if (usedNumbers[i] !== i + 1) {
            next = i + 1;
            break;
        }
        next = usedNumbers.length + 1;
    }
    return next;
};

/**
 * Extracts support users from plan data
 */
export const extractSupportUsers = (supportUsers) => {
    let selectedMarketingMember = null;
    let selectedSupplyChainMember = null;

    if (Array.isArray(supportUsers)) {
        selectedMarketingMember = supportUsers.find(u => u.role === USER_ROLES.MARKETING) || null;
        selectedSupplyChainMember = supportUsers.find(u => u.role === USER_ROLES.SUPPLY_CHAIN) || null;
    }

    return { selectedMarketingMember, selectedSupplyChainMember };
};

/**
 * Maps backend plan data to frontend format
 */
export const mapBackendPlanToFrontend = (plan, idx) => {
    const productIds = Array.isArray(plan.products) ? plan.products.map(p => p.productId) : [];
    const { selectedMarketingMember, selectedSupplyChainMember } = extractSupportUsers(plan.supportUsers);
    const planType = normalizePlanType(plan.planType);

    const formData = {
        planType,
        exitDate: plan.exitDate || null,
        endOfProductionDate: plan.endOfProductionDate || null,
        endOfImportationDate: plan.endOfImportationDate || null,
        replacementPlan: plan.replacementPlan || '',
        liquidationPlan: plan.liquidationPlan || '',
        additionalComments: plan.additionalComments || '',
        needsSupport: plan.marketingSupport || plan.supplyChainSupport || false,
        marketingSupport: plan.marketingSupport || false,
        supplyChainSupport: plan.supplyChainSupport || false,
        selectedMarketingMember,
        selectedSupplyChainMember,
        reviewStatus: plan.status || '',
        reviewComments: plan.approvalComment || '',
        // Exemption-only attributes (empty defaults for exit plans)
        toImpactEuros: plan.toImpactEuros ?? EMPTY_EXEMPTION_FIELDS.toImpactEuros,
        exceptionStartDate: plan.exceptionStartDate || EMPTY_EXEMPTION_FIELDS.exceptionStartDate,
        exceptionEndDate: plan.exceptionEndDate || EMPTY_EXEMPTION_FIELDS.exceptionEndDate,
        justification: plan.justification || EMPTY_EXEMPTION_FIELDS.justification,
        safetyAssessmentReference: plan.safetyAssessmentReference || EMPTY_EXEMPTION_FIELDS.safetyAssessmentReference,
        productTypes: plan.productTypes || EMPTY_EXEMPTION_FIELDS.productTypes,
        brands: plan.brands || EMPTY_EXEMPTION_FIELDS.brands,
        localRiskAssessment: plan.localRiskAssessment || '',
        status: plan.status,
        createdAt: plan.createdAt,
        lastUpdatedAt: plan.lastUpdatedAt
    };

    return {
        id: plan.id,
        planType,
        name: plan.name || `Plan #${idx + 1}`,
        productIds,
        formData
    };
};

/**
 * Prepares support user IDs for API
 */
export const prepareSupportUserIds = (supportState) => {
    const supportUserIds = [];
    if (supportState.marketingSupport && supportState.selectedMarketingMember?.id) {
        supportUserIds.push(supportState.selectedMarketingMember.id);
    }
    if (supportState.supplyChainSupport && supportState.selectedSupplyChainMember?.id) {
        supportUserIds.push(supportState.selectedSupplyChainMember.id);
    }
    return supportUserIds;
};

/**
 * Restriction types for plan configuration
 */
export const RESTRICTION_TYPES = {
    BANNED: 'BANNED',
    RESTRICTED: 'RESTRICTED',
    LABELING_REQUIREMENTS: 'LABELING_REQUIREMENTS'
};
